import React from "react";
import { useToggleModal } from "../../hooks/useToggleModel";
import * as Yup from "yup";
import { useFormik } from "formik";
import axios from "axios";
import { Modal } from "../modal";
import { Input } from "../Input";
import { Button } from "../Button";
import Swal from "sweetalert2";
import { useMutation } from "react-query";
import { Cashier } from "../../types/restaurant";

type SaveInvoiceFromCashierProps = {
  restaurantId: number;
  cashierId: string;
  setSelectedCashier?: (cashier: Cashier | null) => void;
};

export const SaveInvoiceFromCashier: React.FC<SaveInvoiceFromCashierProps> = ({
  restaurantId,
  cashierId,
  setSelectedCashier,
}) => {
  const [openModel, toggleModel] = useToggleModal();

  const { mutateAsync: saveOrder, isLoading: saveOrderLoading } = useMutation(
    async (data: any) => {
      const res = await axios.post("/order/foodics/cashier/add-order", data);

      if (res.status === 200) {
        formik.resetForm();
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "The invoice has been saved successfully.",
          confirmButtonText: "Done",
          confirmButtonColor: "#21D868",
          allowOutsideClick: false,
          allowEscapeKey: false,
          customClass: {
            title: "swal2-title-custom",
            htmlContainer: "swal2-text-custom",
          },
          didOpen: () => {
            const titleEl = document.querySelector(".swal2-title");
            if (titleEl) titleEl.setAttribute("style", "color: #21D868");
            const textEl = document.querySelector(".swal2-html-container");
            if (textEl) textEl.setAttribute("style", "color: #94A3B8;");
          },
        }).then((result) => {
          if (result.isConfirmed) {
            if (setSelectedCashier) {
              setSelectedCashier(null);
            }
          }
        });
      }
      return res.data;
    }
  );

  const formik = useFormik({
    initialValues: {
      phone: "",
    },
    validationSchema: Yup.object({
      phone: Yup.string()
        .matches(/^5\d{8}$/, "Phone number must start with 5 and be 9 digits")
        .required("Phone is required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      Swal.fire({
        title: `Are you sure the Phone is ${values?.phone} ?`,
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#21D868",
        cancelButtonColor: "#FE5960",
        confirmButtonText: "Sure",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await saveOrder({
              phone: values.phone,
              casherId: cashierId,
              restaurantId: restaurantId,
            });
          } catch (error: any) {
            Swal.fire({
              icon: "error",
              title: "Oops!",
              text: error?.response?.data?.message || "Something went wrong.",
              confirmButtonText: "Ok",
              confirmButtonColor: "#FE5960",
              allowOutsideClick: false,
              allowEscapeKey: false,
              customClass: {
                title: "swal2-title-custom",
                htmlContainer: "swal2-text-custom",
              },
              didOpen: () => {
                const titleEl = document.querySelector(".swal2-title");
                if (titleEl) titleEl.setAttribute("style", "color: #FE5960");
                const textEl = document.querySelector(".swal2-html-container");
                if (textEl) textEl.setAttribute("style", "color: #94A3B8;");
              },
            }).then((result) => {
              if (result.isConfirmed) {
                if (setSelectedCashier) {
                  setSelectedCashier(null);
                }
              }
            });
          } finally {
            resetForm();
            toggleModel();
          }
        }
      });
    },
  });

  return (
    <>
      <Button
        className="w-full !mt-6 !bg-primary !text-white !font-bold focus:outline-none focus:ring-0 hover:!bg-primary/80"
        onClick={toggleModel}
      >
        Save Invoice
      </Button>

      <Modal
        title="Save User Invoice"
        handleClose={toggleModel}
        isOpen={openModel}
      >
        <form
          onSubmit={formik.handleSubmit}
          className="w-full first-letter:flex flex-col gap-1 transition-all"
        >
          <label className="text-left w-full text-sm text-green-500 font-semibold">
            Phone Number
          </label>
          <Input
            type="text"
            className="w-full h-10 border-2 border-green-500 text-gray-600 font-medium"
            placeholder="Enter your phone number"
            required
            {...formik.getFieldProps("phone")}
            error={formik.touched.phone ? formik.errors.phone : ""}
          />
          <Button
            className="w-full rounded-lg text-md py-2 mt-6 !bg-primary !text-white !font-bold focus:outline-none focus:ring-0 hover:!bg-primary/80"
            type="submit"
            disabled={
              !formik.values.phone ||
              formik.isSubmitting ||
              saveOrderLoading ||
              !!formik.errors.phone
            }
            loading={saveOrderLoading}
          >
            Save
          </Button>
        </form>
      </Modal>
    </>
  );
};
